import { InstancePlugin } from "@bryntum/schedulerpro";
import {
  handleScheduleEntityFilesDrop,
  confirmScheduleFileDrop,
} from "@/react/utils/fileDropUtils";
import { getSavedBoardSelection } from "@/react/hooks/useBoards.jsx";
import { createScheduleFileDropAttachment } from "./createScheduleFileDropAttachment.js";

/**
 * Shared schedule file-drop InstancePlugin.
 * Surfaces differ only in root, drop-target selector, and task resolution.
 * Attach in construct/paint — SchedulePage addPlugins runs after first paint.
 */
export function createScheduleFileDropPluginClass({
  pluginName,
  getRootElement,
  findDropTarget,
  resolveTask,
}) {
  return class extends InstancePlugin {
    static get $name() {
      return pluginName;
    }

    construct(client, config) {
      super.construct(client, config);

      this.fileDrop = createScheduleFileDropAttachment({
        getRootElement: () => getRootElement(this),
        findDropTarget,
        onFilesDropped: (event, targetEl) => this.uploadDroppedFiles(event, targetEl),
      });

      this.fileDrop.attach();
      this.client.on({ paint: () => this.fileDrop.attach(), thisObj: this });
    }

    async uploadDroppedFiles(event, targetEl) {
      const taskRecord = resolveTask(this, targetEl);
      if (!taskRecord?.id) return;

      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length === 0) return;

      const boardId = getSavedBoardSelection()?.id;
      if (!boardId) return;

      const confirmed = await confirmScheduleFileDrop({
        files,
        taskName: taskRecord.name || "Untitled task",
      });
      if (!confirmed) return;

      await handleScheduleEntityFilesDrop({
        entityType: "task",
        entityId: taskRecord.id,
        boardId,
        files,
      });
    }

    doDestroy() {
      this.fileDrop?.destroy();
      this.fileDrop = null;
      super.doDestroy();
    }
  };
}
